import Num, { BigNumber } from '@flipster/utils/num';

/**
 * close-all이 포지션에서 읽는 최소 구조 (OpenPosition/LegacyOpenPosition 공통 필드).
 * legacy는 quantity/가격이 PrexDecimal(BigNumber)이므로 문자열·숫자·BigNumber를 모두 허용한다.
 */
export interface CloseAllPositionInput {
  symbol: string;
  slot: number;
  side: string;
  quantity: string | BigNumber;
  bidPrice: string | number | BigNumber;
  askPrice: string | number | BigNumber;
}

/** tradable(비 RWA 또는 현재 거래 가능) / suspended(RWA 거래시간 외) 분리. suspended는 API로 보내지 않는다. */
export const splitByTradability = <P extends { symbol: string }>(
  positions: P[],
  isTradable: (symbol: string) => boolean,
): { tradablePositions: P[]; suspendedPositions: P[] } => ({
  tradablePositions: positions.filter((p) => isTradable(p.symbol)),
  suspendedPositions: positions.filter((p) => !isTradable(p.symbol)),
});

/** 수량 오름차순 정렬 (기존 close-all 전송 순서 보존). 원본 배열은 변경하지 않는다. */
export const sortByQuantityAsc = <P extends Pick<CloseAllPositionInput, 'quantity'>>(positions: P[]): P[] =>
  [...positions].sort((a, b) => Num(a.quantity).minus(b.quantity).toNumber());

/** 종료 주문 필드: side 반전 + Long→bid/Short→ask 가격 선택(문자열화). */
export const toCloseOrderFields = (
  p: CloseAllPositionInput,
): { symbol: string; side: 'Long' | 'Short'; price: string } => ({
  symbol: p.symbol,
  side: p.side === 'Long' ? 'Short' : 'Long',
  price: (p.side === 'Long' ? p.bidPrice : p.askPrice).toString(),
});

export interface CloseAllResponsePosition {
  symbol: string;
  status: string;
}

export type CloseAllOutcome<P> =
  | { kind: 'allFailed' }
  | { kind: 'someFailed'; failedCount: number; totalCount: number }
  | { kind: 'allPartial' }
  | { kind: 'partialAndSuccess'; partialCount: number; successCount: number }
  | { kind: 'partialAndFailed'; partialCount: number; failedCount: number }
  | { kind: 'partialFailedSuccess'; partialCount: number; failedCount: number; successCount: number }
  | { kind: 'suspendedRemainder'; suspendedPositions: P[]; closedItems: CloseAllResponsePosition[] }
  | { kind: 'success' };

/**
 * close-all 응답을 결과 케이스로 분류한다 — 기존 use-close-positions의 alert 6분기 + suspended 노티스 +
 * 성공 토스트 판정 순서를 그대로 보존한 순수 분류기. failedCount는 tradable 기준(suspended는 미전송).
 */
export const classifyCloseAllOutcome = <P>(
  resPositions: CloseAllResponsePosition[],
  tradablePositions: { length: number },
  suspendedPositions: P[],
): CloseAllOutcome<P> => {
  const successCount = resPositions.filter((p) => p.status === 'Success').length;
  const partialCount = resPositions.filter((p) => p.status === 'PartiallyFilled').length;
  const failedCount = tradablePositions.length - successCount - partialCount;

  if (partialCount === 0 && failedCount > 0 && successCount === 0) return { kind: 'allFailed' };
  if (partialCount === 0 && failedCount > 0 && successCount > 0) {
    return { kind: 'someFailed', failedCount, totalCount: tradablePositions.length };
  }
  if (partialCount > 0 && failedCount === 0 && successCount === 0) return { kind: 'allPartial' };
  if (partialCount > 0 && failedCount === 0 && successCount > 0) {
    return { kind: 'partialAndSuccess', partialCount, successCount };
  }
  if (partialCount > 0 && failedCount > 0 && successCount === 0) {
    return { kind: 'partialAndFailed', partialCount, failedCount };
  }
  if (partialCount > 0 && failedCount > 0 && successCount > 0) {
    return { kind: 'partialFailedSuccess', partialCount, failedCount, successCount };
  }
  if (suspendedPositions.length > 0) {
    return {
      kind: 'suspendedRemainder',
      suspendedPositions,
      closedItems: resPositions.filter((p) => p.status === 'Success' || p.status === 'PartiallyFilled'),
    };
  }
  return { kind: 'success' };
};
